import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import abwesenheitService from '../services/abwesenheitService';
import zeiterfassungService from '../services/zeiterfassungService';
import { Icon, PageHeader, EmptyState, FilterZuruecksetzen, filterAktiv, fehlerText } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import {
  Abwesenheit, AbwesenheitKonflikt, AbwesenheitStatus, AbwesenheitTyp, UrlaubSaldo, Mitarbeiter,
} from '../types';
import { DatumFeld } from '../components/DatumZeit';

const heute = () => new Date().toLocaleDateString('sv-SE');
const fdate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('de-DE');

const TYP_LABEL: Record<AbwesenheitTyp, string> = {
  URLAUB: 'Urlaub', KRANKHEIT: 'Krankheit', UNBEZAHLT: 'Unbezahlt', SONDERURLAUB: 'Sonderurlaub',
};
const STATUS_BADGE: Record<AbwesenheitStatus, string> = {
  BEANTRAGT: 'bg-amber-100 text-amber-700',
  GENEHMIGT: 'bg-green-100 text-green-700',
  ABGELEHNT: 'bg-red-100 text-red-700',
  STORNIERT: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL: Record<AbwesenheitStatus, string> = {
  BEANTRAGT: 'Beantragt', GENEHMIGT: 'Genehmigt', ABGELEHNT: 'Abgelehnt', STORNIERT: 'Storniert',
};

/** Status-Badge; blendet nur bei gerade geänderten (markierten) Anträgen weich ein. */
const StatusBadge: React.FC<{ a: Abwesenheit }> = ({ a }) => {
  const markiert = ankunft(`abw-${a.id}`).className !== '';
  return (
    <span key={markiert ? a.status : undefined} className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status]} ${markiert ? 'ui-wechsel' : ''}`}>
      {STATUS_LABEL[a.status]}
    </span>
  );
};

const Urlaub: React.FC = () => {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  // Mitarbeiter-Sicht
  const [saldo, setSaldo] = useState<UrlaubSaldo | null>(null);
  const [meine, setMeine] = useState<Abwesenheit[]>([]);
  const [von, setVon] = useState(heute());
  const [bis, setBis] = useState(heute());
  const [halberTag, setHalberTag] = useState(false);
  const [grund, setGrund] = useState('');

  // Admin-Sicht
  const [users, setUsers] = useState<Mitarbeiter[]>([]);
  const [alle, setAlle] = useState<Abwesenheit[]>([]);
  const [filterUser, setFilterUser] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<AbwesenheitStatus | ''>('');
  const [konfliktModal, setKonfliktModal] = useState<{ id: number; konflikte: AbwesenheitKonflikt[] } | null>(null);

  const ladeMeine = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([abwesenheitService.meinSaldo(), abwesenheitService.meine()]);
      setSaldo(s); setMeine(m);
    } catch { /* ignore */ }
  }, []);

  const ladeAlle = useCallback(async () => {
    if (!admin) return;
    try {
      setAlle(await abwesenheitService.alle(filterUser === '' ? undefined : filterUser, filterStatus || undefined));
    } catch { /* ignore */ }
  }, [admin, filterUser, filterStatus]);

  useEffect(() => { ladeMeine(); }, [ladeMeine]);
  useEffect(() => { if (admin) zeiterfassungService.getAktiveMitarbeiter().then(setUsers).catch(() => {}); }, [admin]);
  useEffect(() => { ladeAlle(); }, [ladeAlle]);

  const antragStellen = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const neu = await abwesenheitService.antrag({ von, bis, halberTag, antragGrund: grund || undefined });
      toast.success('Urlaubsantrag gestellt');
      if (neu?.id) markiereNeu(`abw-${neu.id}`);
      setGrund(''); setHalberTag(false);
      await ladeMeine(); await ladeAlle();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Urlaubsantrag konnte nicht gestellt werden'));
    }
  };

  const stornieren = async (id: number) => {
    if (!window.confirm('Urlaubsantrag stornieren?')) return;
    try { await abwesenheitService.stornieren(id); toast.success('Urlaubsantrag storniert'); markiereNeu(`abw-${id}`); await ladeMeine(); await ladeAlle(); }
    catch (e: any) { toast.error(fehlerText(e, 'Urlaubsantrag konnte nicht storniert werden')); }
  };

  const pruefeGenehmigen = async (id: number) => {
    try {
      const konflikte = await abwesenheitService.konflikte(id);
      if (konflikte.length > 0) { setKonfliktModal({ id, konflikte }); return; }
      await genehmigen(id);
    } catch (e) { toast.error(fehlerText(e, 'Konflikte konnten nicht geprüft werden')); }
  };

  const genehmigen = async (id: number) => {
    try {
      await abwesenheitService.genehmigen(id);
      toast.success('Urlaubsantrag genehmigt');
      markiereNeu(`abw-${id}`);
      setKonfliktModal(null);
      await ladeAlle(); await ladeMeine();
    } catch (e) { toast.error(fehlerText(e, 'Urlaubsantrag konnte nicht genehmigt werden')); }
  };

  const ablehnen = async (id: number) => {
    const g = window.prompt('Begründung der Ablehnung:');
    if (g == null || !g.trim()) return;
    try { await abwesenheitService.ablehnen(id, g.trim()); toast.success('Urlaubsantrag abgelehnt'); markiereNeu(`abw-${id}`); await ladeAlle(); await ladeMeine(); }
    catch (e: any) { toast.error(fehlerText(e, 'Urlaubsantrag konnte nicht abgelehnt werden')); }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader title="Urlaub & Abwesenheiten" className="!mb-0" />

      {/* Saldo */}
      {saldo && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <div className="bg-white rounded-lg shadow p-3 md:p-4"><p className="text-xs text-gray-500">Anspruch {saldo.jahr}</p><p className="text-xl font-bold text-gray-900">{saldo.anspruchTage + saldo.uebertragTage} T</p>{saldo.uebertragTage > 0 && <p className="text-xs text-gray-400">inkl. {saldo.uebertragTage} Übertrag</p>}</div>
          <div className="bg-white rounded-lg shadow p-3 md:p-4"><p className="text-xs text-gray-500">Genommen</p><p className="text-xl font-bold text-gray-900">{saldo.genommenTage} T</p></div>
          <div className="bg-white rounded-lg shadow p-3 md:p-4"><p className="text-xs text-gray-500">Beantragt</p><p className="text-xl font-bold text-amber-600">{saldo.beantragtTage} T</p></div>
          <div className="bg-brand-600 text-white rounded-lg shadow p-3 md:p-4"><p className="text-xs text-white/80">Resturlaub</p><p className="text-xl font-bold">{saldo.restTage} T</p></div>
        </div>
      )}

      {/* Antrag */}
      <form onSubmit={antragStellen} className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-4">
        <div><label className="block text-sm font-medium mb-1">Von</label><DatumFeld value={von} onChange={(v) => { setVon(v); if (bis < v) setBis(v); }} className="p-2 border border-gray-300 rounded-lg" /></div>
        <div><label className="block text-sm font-medium mb-1">Bis</label><DatumFeld value={bis} min={von} onChange={(v) => setBis(v)} className="p-2 border border-gray-300 rounded-lg" /></div>
        <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={halberTag} disabled={von !== bis} onChange={(e) => setHalberTag(e.target.checked)} /> Halber Tag</label>
        <div className="flex-1 min-w-[180px]"><label className="block text-sm font-medium mb-1">Grund (optional)</label><input type="text" value={grund} onChange={(e) => setGrund(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg" /></div>
        <button type="submit" className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">Urlaub beantragen</button>
      </form>

      {/* Meine Anträge */}
      <div className="bg-white rounded-lg shadow">
        <h2 className="font-semibold text-gray-900 p-4 border-b">Meine Anträge</h2>
        {meine.length === 0 ? <EmptyState icon={<Icon.Urlaub className="h-6 w-6" />} title="Noch keine Anträge" hint="Zeitraum oben wählen und Urlaub beantragen." /> : (
          <div className="divide-y divide-gray-100">
            {meine.map((a) => { const glow = ankunft(`abw-${a.id}`); return (
              <div key={a.id} ref={glow.ref} className={`p-4 flex items-center justify-between gap-3 ${glow.className}`}>
                <div>
                  <p className="text-sm font-medium text-gray-900">{TYP_LABEL[a.typ]}: {fdate(a.von)} – {fdate(a.bis)} <span className="text-gray-400">({a.tage} {a.tage === 1 ? 'Tag' : 'Tage'})</span></p>
                  {a.ablehnungsGrund && <p className="text-xs text-red-600">Ablehnung: {a.ablehnungsGrund}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge a={a} />
                  {(a.status === 'BEANTRAGT' || a.status === 'GENEHMIGT') && (
                    <button onClick={() => stornieren(a.id)} className="text-xs text-gray-500 hover:text-red-600">Stornieren</button>
                  )}
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>

      {/* Admin */}
      {admin && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900">Alle Anträge</h2>
            <div className="flex flex-wrap gap-2">
              <select value={filterUser} onChange={(e) => setFilterUser(e.target.value === '' ? '' : Number(e.target.value))} className={`p-2 border border-gray-300 rounded-lg text-sm ${filterUser !== '' ? filterAktiv : ''}`}>
                <option value="">Alle Mitarbeiter</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as AbwesenheitStatus | '')} className={`p-2 border border-gray-300 rounded-lg text-sm ${filterStatus ? filterAktiv : ''}`}>
                <option value="">Alle Status</option>
                {(['BEANTRAGT', 'GENEHMIGT', 'ABGELEHNT', 'STORNIERT'] as AbwesenheitStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          {alle.length === 0 ? (
            filterUser !== '' || filterStatus
              ? <EmptyState title="Keine Anträge für diesen Filter" action={<FilterZuruecksetzen onClick={() => { setFilterUser(''); setFilterStatus(''); }} />} />
              : <EmptyState icon={<Icon.Urlaub className="h-6 w-6" />} title="Keine Anträge" />
          ) : (
            <div className="divide-y divide-gray-100">
              {alle.map((a) => { const glow = ankunft(`abw-${a.id}`); return (
                <div key={a.id} ref={glow.ref} className={`p-4 flex flex-wrap items-center justify-between gap-3 ${glow.className}`}>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{a.mitarbeiterName} <span className="font-normal text-gray-500">· {TYP_LABEL[a.typ]}</span></p>
                    <p className="text-xs text-gray-500">{fdate(a.von)} – {fdate(a.bis)} ({a.tage} {a.tage === 1 ? 'Tag' : 'Tage'}){a.antragGrund ? ` · ${a.antragGrund}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge a={a} />
                    {a.status === 'BEANTRAGT' && (
                      <>
                        <button onClick={() => pruefeGenehmigen(a.id)} className="text-xs px-2 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700">Genehmigen</button>
                        <button onClick={() => ablehnen(a.id)} className="text-xs px-2 py-1 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">Ablehnen</button>
                      </>
                    )}
                  </div>
                </div>
              ); })}
            </div>
          )}
        </div>
      )}

      {/* Konflikt-Modal */}
      {konfliktModal && (
        <div className="ui-fade fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={(e) => { if (e.target === e.currentTarget) setKonfliktModal(null); }}>
          <div className="ui-pop bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Icon.Warnung className="h-5 w-5 text-amber-500" />Terminkonflikt</h2>
            <p className="text-sm text-gray-600 mb-3">Folgende Mitarbeiter sind im selben Zeitraum abwesend:</p>
            <ul className="space-y-1 mb-4 max-h-48 overflow-y-auto">
              {konfliktModal.konflikte.map((k, i) => (
                <li key={i} className="text-sm text-gray-800 flex justify-between border-b border-gray-100 py-1">
                  <span>{k.mitarbeiterName} ({TYP_LABEL[k.typ]})</span>
                  <span className="text-gray-500">{fdate(k.von)}–{fdate(k.bis)} · {STATUS_LABEL[k.status]}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button onClick={() => setKonfliktModal(null)} className="px-3 py-1.5 rounded-lg border text-sm">Abbrechen</button>
              <button onClick={() => genehmigen(konfliktModal.id)} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm">Trotzdem genehmigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Urlaub;
